import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, dataBr, dataHoraBr, ErroDoPortal } from '../api';

export interface OsResumo {
  os_id: string;
  numero: string;
  obra_codigo: string;
  obra_nome: string;
  municipio: string | null;
  uf: string | null;
  cliente: string;
  proposta_numero: string | null;
  prazo_contratual: string | null;
  situacao: string;
  documentos: string;
  ultima_entrega: string | null;
}

/** Estado com palavra, forma e cor — nessa ordem de importância (§18). */
const CLASSE_ESTADO: Record<string, string> = {
  'Em andamento': 'andamento',
  'Serviços concluídos': 'concluida',
  Suspensa: 'parada',
  Cancelada: 'parada',
};

/**
 * A lista de ordens de serviço.
 *
 * ── Por que a organização é por OS ───────────────────────────────────
 *
 * Foi a decisão da Testesolo, e casa com como o cliente pensa: ele
 * contratou um serviço para uma obra, e é por obra que procura. Uma
 * lista plana de duzentos laudos ordenada por data seria mais fácil de
 * programar e impossível de usar.
 *
 * Só aparecem OS com pelo menos um documento entregue — a vista do banco
 * já cuida disso. Uma OS vazia aqui seria uma pasta que o cliente abre
 * por engano toda semana.
 */
export function Acervo() {
  const [linhas, setLinhas] = useState<OsResumo[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const corte = new AbortController();
    api<OsResumo[]>('/os', { sinal: corte.signal })
      .then(setLinhas)
      .catch((e) => {
        if ((e as Error)?.name === 'AbortError') return;
        setErro(e instanceof ErroDoPortal ? e.message : 'Não foi possível carregar.');
      });
    return () => corte.abort();
  }, []);

  if (erro) return <div className="aviso ruim" role="alert">{erro}</div>;
  if (!linhas) return <p className="sub">Carregando…</p>;

  return (
    <div>
      <h1>Ordens de serviço</h1>
      <p className="linha-obra">
        {linhas.length === 0
          ? 'Nada por aqui ainda.'
          : `${linhas.length} ordem(ns) com documentos disponíveis`}
      </p>

      {linhas.length === 0 ? (
        <div className="cartao-os" style={{ cursor: 'default' }}>
          <p>
            Assim que a Testesolo disponibilizar um relatório para você,
            ele aparece nesta tela.
          </p>
          <p className="sub">
            Se está esperando algo que não chegou, fale com a gente pelo
            (47) 3037-2817.
          </p>
        </div>
      ) : (
        <ul className="lista-os">
          {linhas.map((o) => (
            <li key={o.os_id}>
              <Link to={`/os/${o.os_id}`} className="cartao-os">
                <div className="cartao-topo">
                  <h2>{o.obra_nome}</h2>
                  <span className={`estado ${CLASSE_ESTADO[o.situacao] ?? 'parada'}`}>
                    {o.situacao}
                  </span>
                </div>
                {/*
                  * Rótulo e valor, como no bloco de identificação do
                  * documento — e não uma linha de metadados separada por
                  * pontos, que obriga a decifrar qual número é qual.
                  */}
                <div className="cartao-metas">
                  <div>
                    <span className="rot">Ordem de serviço</span>
                    <span className="val mono">{o.numero}</span>
                  </div>
                  <div>
                    <span className="rot">Local</span>
                    <span className="val">
                      {o.municipio ? `${o.municipio}/${o.uf ?? ''}` : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="rot">Documentos</span>
                    <span className="val mono">{o.documentos}</span>
                  </div>
                  <div>
                    <span className="rot">Última entrega</span>
                    <span className="val mono">{dataHoraBr(o.ultima_entrega)}</span>
                  </div>
                  <div>
                    <span className="rot">Prazo contratual</span>
                    <span className="val mono">{dataBr(o.prazo_contratual)}</span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
